import { Button, Form, Input, Select, message } from 'antd'
import { useEffect, useState } from 'react'
import {
    fetchEpics,
    fetchSprints,
    fetchTags,
    getCurrentProjectId,
    isValidArray,
    isValidObject,
    useAsyncSelect,
} from './helper'
import {
    createTestCase,
    updateTestCase,
} from '../../../../redux/testCases/testCase.service'
import CreateTagForm from './CreateTagForm'
import { PlusOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import {
    testCaseDetail,
    testCaseState,
} from '../../../../redux/testCases/testCaseList.slice'
import SkeletonTestCaseForm from './SkeletonTestCaseForm'
import AsyncTagField from '../list/filter/AsyncTagField'

const { TextArea } = Input

function TestCaseForm(props) {
    const {
        stateTestCase = {},
        setStateTestCase = () => {},
        handleTestCaseList = () => {},
        handleClose = () => {},
    } = props

    const [form] = Form.useForm()
    const dispatch = useDispatch()

    const {
        testCaseDetailLoading = false,
        testCaseDetail: testCaseDetailData = {},
    } = useSelector(testCaseState)

    const [submitType, setSubmitType] = useState('close')
    const [isCreateTag, setCreateTag] = useState(false)
    const [saveLoading, setSaveLoading] = useState({
        saveAndNext: false,
        saveAndClose: false,
    })

    const isEdit = stateTestCase?.action === 'edit'
    const activeId = stateTestCase?.activeId || ''

    // hook up 3 async selects
    const epicSelect = useAsyncSelect(fetchEpics)
    const sprintSelect = useAsyncSelect(fetchSprints)
    const tagSelect = useAsyncSelect(fetchTags)

    const handleSubmit = async (values) => {
        setSaveLoading({
            saveAndClose: submitType === 'close',
            saveAndNext: submitType === 'next',
        })
        if (isEdit) {
            const payload = {
                id: activeId,
                data: {
                    title: values?.title || '',
                    preconditions: values?.preconditions || '',
                    testData: values?.testData || '',
                    project: getCurrentProjectId(),
                    steps: values?.steps || '',
                    description: values?.description || '',
                    expectedResult: values?.expectedResult || '',
                    ...(isValidObject(values?.epic) || values?.epic
                        ? {
                              epic: isValidObject(values?.epic)
                                  ? values?.epic?._id
                                  : values?.epic,
                          }
                        : {}),

                    sprint: isValidObject(values?.sprint)
                        ? values?.sprint?._id
                        : values?.sprint,
                    tags: isValidArray(values?.tags)
                        ? values?.tags?.map((item) => item?.value || item)
                        : [],
                },
            }

            const resp = await updateTestCase(payload)

            if (resp?.data?.success) {
                message.success(
                    resp?.data?.message || 'Test case submitted successfully!'
                )
                setStateTestCase({
                    ...stateTestCase,
                    drawerOpen: false,
                    action: 'add',
                    activeId: '',
                })
                handleTestCaseList()
                form.resetFields()
            } else {
                message.error(
                    resp?.data?.message ||
                        'Failed to submit test case. Please try again.'
                )
            }
            setSaveLoading({
                saveAndClose: false,
                saveAndNext: false,
            })
        } else {
            const payload = {
                title: values?.title || '',
                preconditions: values?.preconditions || '',
                testData: values?.testData || '',
                project: getCurrentProjectId(),
                steps: values?.steps || '',
                description: values?.description || '',
                expectedResult: values?.expectedResult || '',
                ...(values?.epic ? { epic: values?.epic || '' } : {}),

                sprint: values?.sprint || '',
                tags: isValidArray(values?.tags)
                    ? values?.tags?.map((item) => item?.value || item)
                    : [],
            }
            const resp = await createTestCase(payload)

            if (resp?.data?.success) {
                message.success(
                    resp?.data?.message || 'Test case submitted successfully!'
                )
                if (submitType === 'close') {
                    setStateTestCase({
                        ...stateTestCase,
                        drawerOpen: false,
                        action: 'add',
                        activeId: '',
                    })
                } else {
                    form.resetFields()
                }
                handleTestCaseList()
            } else {
                message.error(
                    resp?.data?.message ||
                        'Failed to submit test case. Please try again.'
                )
            }
            setSaveLoading({
                saveAndClose: false,
                saveAndNext: false,
            })
        }
    }

    useEffect(() => {
        setCreateTag(false)
        if (isEdit) {
            // load edit data here

            dispatch(testCaseDetail(activeId)).then((resp) => {
                if (isValidObject(resp?.payload?.data)) {
                    const detailsData = resp?.payload?.data || {}

                    // form.resetFields()
                    // setTimeout(() => {
                    // form.setFieldValue('tags', finalOptions)
                    // }, 100)
                    // set initial field values (ids)
                    form.setFieldsValue({
                        title: detailsData?.title || '',
                        description: detailsData?.description || '',
                        preconditions: detailsData?.preconditions || '',
                        testData: detailsData?.testData || '',
                        steps: detailsData?.steps || '',
                        expectedResult: detailsData?.expectedResult || '',
                        status: detailsData?.status || '',
                        epic: getOptions(detailsData?.epic) || '',
                        sprint: getOptions(detailsData?.sprint) || null,
                        tags: isValidArray(detailsData?.tags)
                            ? getOptionsForMulti(detailsData?.tags)
                            : [],
                    })
                }
            })
        }
        return () => {
            form.resetFields()
            setCreateTag(false)
        }
    }, [stateTestCase])

    const getOptionsForMulti = (option) => {
        if (isValidArray(option)) {
            const finalData = option?.map((item) => {
                return {
                    label: item?.name || '',
                    value: item?._id || '',
                }
            })
            return finalData
        }
        return []
    }

    const getOptions = (option) => {
        if (isValidObject(option)) {
            return {
                label: option?.title || '',
                value: option?._id || '',
            }
        }
        return null
    }

    const isShowForm =
        !isEdit || (!testCaseDetailLoading && isValidObject(testCaseDetailData))

    return (
        <div
            style={{
                maxWidth: 700,
                margin: '0 auto',
                paddingLeft: '20px',
                paddingRight: '20px',
                paddingTop: '20px',
                background: '#fff',
                borderRadius: '8px',
            }}
        >
            {testCaseDetailLoading && isEdit && <SkeletonTestCaseForm />}
            {isShowForm && (
                <>
                    <Form
                        requiredMark={false}
                        form={form}
                        layout="horizontal"
                        labelCol={{ span: 6 }}
                        wrapperCol={{ span: 18 }}
                        labelAlign="left"
                        onFinish={handleSubmit}
                        initialValues={{
                            title: isEdit ? testCaseDetailData.title || '' : '',
                            description: isEdit
                                ? testCaseDetailData.description
                                : '',
                            preconditions: isEdit
                                ? testCaseDetailData.preconditions || ''
                                : '',
                            testData: isEdit
                                ? testCaseDetailData.testData || ''
                                : '',
                            steps: isEdit ? testCaseDetailData.steps || '' : '',
                            expectedResult: isEdit
                                ? testCaseDetailData.expectedResult || ''
                                : '',
                            status: isEdit
                                ? testCaseDetailData.status || ''
                                : '',
                            sprint: isEdit
                                ? getOptions(testCaseDetailData.sprint) || null
                                : null,
                            epic: isEdit
                                ? getOptions(testCaseDetailData?.epic) || ''
                                : '',
                            tags:
                                isEdit && isValidArray(testCaseDetailData?.tags)
                                    ? getOptionsForMulti(
                                          testCaseDetailData?.tags || []
                                      )
                                    : [],
                        }}
                    >
                        {/* Title */}
                        <Form.Item
                            label={
                                <span>
                                    Title{' '}
                                    <span style={{ color: 'red' }}>*</span>
                                </span>
                            }
                            name="title"
                            rules={[
                                {
                                    required: true,
                                    message: 'Title is required',
                                },
                            ]}
                            style={{ marginBottom: 16 }}
                        >
                            <Input placeholder="Enter title" />
                        </Form.Item>

                        {/* Preconditions */}
                        <Form.Item
                            label="Preconditions"
                            name="preconditions"
                            style={{ marginBottom: 16 }}
                        >
                            <TextArea
                                rows={4}
                                placeholder="Enter preconditions"
                            />
                        </Form.Item>

                        {/* Test Data */}
                        <Form.Item
                            label="Test Data"
                            name="testData"
                            style={{ marginBottom: 16 }}
                        >
                            <TextArea rows={4} placeholder="Enter test data" />
                        </Form.Item>

                        {/* Steps */}
                        <Form.Item
                            label="Steps"
                            name="steps"
                            style={{ marginBottom: 16 }}
                        >
                            <TextArea rows={4} placeholder="Enter steps" />
                        </Form.Item>

                        {/* Description */}
                        <Form.Item
                            label="Description"
                            name="description"
                            style={{ marginBottom: 16 }}
                        >
                            <TextArea
                                rows={4}
                                placeholder="Enter description"
                            />
                        </Form.Item>

                        {/* Expected Result */}
                        <Form.Item
                            label="Expected Result"
                            name="expectedResult"
                            style={{ marginBottom: 16 }}
                        >
                            <TextArea
                                rows={4}
                                placeholder="Enter expected result"
                            />
                        </Form.Item>

                        {/* EPIC */}
                        {/* <Form.Item
                            label="Epic"
                            name="epic"
                            rules={[
                                {
                                    required: false,
                                    message: 'Please select an Epic',
                                },
                            ]}
                            style={{ marginBottom: 16 }}
                        >
                            <Select
                                className="reports_project_selecter"
                                allowClear
                                showSearch
                                placeholder="Search & select Epic"
                                filterOption={false}
                                onSearch={epicSelect.onSearch}
                                // onDropdownVisibleChange={
                                //     epicSelect.onDropdownVisibleChange
                                // }
                                onOpenChange={
                                    epicSelect.onDropdownVisibleChange
                                }
                                onPopupScroll={epicSelect.onPopupScroll}
                                options={epicSelect.options}
                                loading={epicSelect.loading}
                                notFoundContent={epicSelect.notFoundContent}
                                optionLabelProp="label"
                            />
                        </Form.Item> */}

                        {/* SPRINT */}
                        <Form.Item
                            label={
                                <span>
                                    Sprint{' '}
                                    <span style={{ color: 'red' }}>*</span>
                                </span>
                            }
                            name="sprint"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please select a Sprint',
                                },
                            ]}
                            style={{ marginBottom: 16 }}
                        >
                            <Select
                                className="reports_project_selecter"
                                allowClear
                                showSearch
                                placeholder="Search & select Sprint"
                                filterOption={false}
                                onSearch={sprintSelect.onSearch}
                                onOpenChange={
                                    sprintSelect.onDropdownVisibleChange
                                }
                                onPopupScroll={sprintSelect.onPopupScroll}
                                options={sprintSelect.options}
                                loading={sprintSelect.loading}
                                notFoundContent={sprintSelect.notFoundContent}
                                optionLabelProp="label"
                                // fieldNames={{ label: 'title', value: '_id' }}
                            />
                        </Form.Item>

                        {/* TAGS */}

                        <div style={{ marginBottom: 16 }}>
                            <AsyncTagField
                                form={form}
                                label={
                                    <span>
                                        Tags{' '}
                                        <span style={{ color: 'red' }}>*</span>
                                    </span>
                                }
                            />
                        </div>

                        {/* Sticky actions */}
                        <div
                            style={{
                                position: 'sticky',
                                bottom: 0,
                                background: '#fff',
                                padding: '10px 0',
                                borderTop: '1px solid #f0f0f0',
                                width: '100%',
                                zIndex: 99,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'end',
                                    width: '100%',
                                    gap: '10px',
                                }}
                            >
                                <Button
                                    type="default"
                                    htmlType="submit"
                                    loading={saveLoading?.saveAndClose}
                                    onClick={() => setSubmitType('close')}
                                >
                                    {isEdit ? 'Update' : 'Add'}
                                </Button>
                                {!isEdit && (
                                    <Button
                                        type="default"
                                        htmlType="submit"
                                        loading={saveLoading?.saveAndNext}
                                        onClick={() => setSubmitType('next')}
                                    >
                                        Add & Next
                                    </Button>
                                )}
                                <Button
                                    type="default"
                                    htmlType="button"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </Form>
                </>
            )}
        </div>
    )
}

export default TestCaseForm
